import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

// --- RxJs
import { catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

// --- 3rd Party
import { TranslateService } from '@ngx-translate/core';
import { retryBackoff } from 'backoff-rxjs';

// --- HS Code
import { environment } from '../../../../environments/environment';
import {
  ApplicationError,
  ErrorMessage,
  PatchModel,
  PatchOperations,
  TermsAndConditionsModel,
} from '../../../models';
import { TermsAndConditionsBaseService } from './terms-and-conditions-base.service';
import { AppService } from '../app.service';

// #endregion

@Injectable({
  providedIn: 'root',
})
export class TermsAndConditionsService extends TermsAndConditionsBaseService {
  private termsAgreementId: string | undefined;
  private userPreferenceEmailOptInId: string | undefined;

  constructor(
    private httpClient: HttpClient,
    private translate: TranslateService,
    appService: AppService,
  ) {
    super(appService);
  }

  clearState(): void {
    this._tacError$.next('');
    this._termsAndConditions$.next(null);
    this._isUpdating$.next(false);
  }

  loadUserAgreementPreferences(): void {
    const url =
      environment.apiConfig.surveyApiUrl + environment.apiConfig.agreementApi;

    this.httpClient
      .get<TermsAndConditionsModel>(url)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._modelError.bind(this))
      )
      .subscribe((termsAndConditions: TermsAndConditionsModel | null) => {
        if (!termsAndConditions) return;

        this._termsAndConditions$.next(termsAndConditions);
        this._shouldShowTaCForm$.next(!termsAndConditions.hasAcceptedTerms);
        this._showHasAcceptedTerms$.next(termsAndConditions.hasAcceptedTerms);
        this._showEmailOptIn$.next(termsAndConditions.emailOptIn);
        this.termsAgreementId = termsAndConditions.termsAgreementId;
        this.userPreferenceEmailOptInId =
          termsAndConditions.userPreferenceEmailOptInId;
      });
  }

  updateUserAgreementSelection(model: TermsAndConditionsModel): void {
    if (!model || !model.hasAcceptedTerms || !this.termsAgreementId) {
      return;
    }

    this._sendUserPreferenceSelection(
      this.termsAgreementId,
      model.hasAcceptedTerms,
      'Terms'
    );
  }

  updateUserEmailOptInSelection(model: TermsAndConditionsModel): void {
    if (!model || model.emailOptIn == null || !this.userPreferenceEmailOptInId) {
      return;
    }

    this._sendUserPreferenceSelection(
      this.userPreferenceEmailOptInId,
      model.emailOptIn,
      'EmailOptIn'
    );
  }

  private _sendUserPreferenceSelection(
    id: string,
    value: boolean,
    valueToUpdate: string
  ): void {
    if (!id) {
      return;
    }

    const agreementUrl =
      environment.apiConfig.surveyApiUrl +
      environment.apiConfig.agreementApi +
      `/${id}`;
    this._isUpdating$.next(true);

    const model = {
      operation: PatchOperations.replace,
      path: valueToUpdate,
      value: value.toString(),
    } as PatchModel;

    this.httpClient
      .patch<TermsAndConditionsModel>(agreementUrl, model)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._modelError.bind(this))
      )
      .subscribe(() => {
        this._isUpdating$.next(false);
      });
  }

  private _modelError(err: any): Observable<TermsAndConditionsModel | null> {
    this._handleError(err);
    return of(null);
  }

  private _handleError(err: any): void {
    this._isUpdating$.next(false);
    this._tacError$.next(err);
    this.hideTaCForm();

    this.appService.setLoading(false);

    const error: ApplicationError = ApplicationError.create(
      ErrorMessage.genericErrorCode,
      'TermsAndConditionsService',
      false
    );
    this.appService.setLastError(error);
  }
}
